import React, { useState } from 'react';
import { TagsList } from './TagsList';
import { TagForm } from './TagForm';
import { TagTypesList } from './TagTypesList';
import { TagTypeForm } from './TagTypeForm';
import { ChevronRightIcon, TagIcon, BookmarkIcon } from 'lucide-react';
export type TagScope = 'Global' | 'OrgUnit';
export type Tag = {
  id: string;
  name: string;
  description: string;
  tagTypeId: string | null;
  tagTypeName: string | null;
  scope: TagScope;
  orgUnitId?: string;
  orgUnitName?: string;
  orgUnitPath?: string;
  colourHex: string;
  isActive: boolean;
  activeFrom: string | null;
  activeTo: string | null;
  createdAt: string;
  updatedAt: string;
};
export type TagType = {
  id: string;
  name: string;
  description: string;
  scope: TagScope;
  orgUnitId?: string;
  orgUnitName?: string;
  orgUnitPath?: string;
  isRestricted: boolean;
  colourPalette: string[] | null;
  createdAt: string;
  updatedAt: string;
};
export function TagsManagement({
  locale = 'en-US'
}) {
  const [activeView, setActiveView] = useState<'tags' | 'tagTypes'>('tags');
  const [showTagForm, setShowTagForm] = useState(false);
  const [showTagTypeForm, setShowTagTypeForm] = useState(false);
  const [editingTag, setEditingTag] = useState<Tag | null>(null);
  const [editingTagType, setEditingTagType] = useState<TagType | null>(null);
  const [tags, setTags] = useState<Tag[]>([{
    id: 'tag-001',
    name: 'Urgent',
    description: 'High priority items requiring immediate attention',
    tagTypeId: 'tagtype-001',
    tagTypeName: 'Priority',
    scope: 'Global',
    colourHex: '#EF4444',
    isActive: true,
    activeFrom: '2023-01-01',
    activeTo: null,
    createdAt: '2023-01-01T00:00:00Z',
    updatedAt: '2023-01-01T00:00:00Z'
  }, {
    id: 'tag-002',
    name: 'Medium',
    description: 'Standard priority items',
    tagTypeId: 'tagtype-001',
    tagTypeName: 'Priority',
    scope: 'Global',
    colourHex: '#F59E0B',
    isActive: true,
    activeFrom: '2023-01-01',
    activeTo: null,
    createdAt: '2023-01-01T00:00:00Z',
    updatedAt: '2023-01-01T00:00:00Z'
  }, {
    id: 'tag-003',
    name: 'Low',
    description: 'Lower priority items',
    tagTypeId: 'tagtype-001',
    tagTypeName: 'Priority',
    scope: 'Global',
    colourHex: '#10B981',
    isActive: true,
    activeFrom: '2023-01-01',
    activeTo: null,
    createdAt: '2023-01-01T00:00:00Z',
    updatedAt: '2023-01-01T00:00:00Z'
  }, {
    id: 'tag-004',
    name: 'IT Project',
    description: 'Related to IT infrastructure projects',
    tagTypeId: 'tagtype-002',
    tagTypeName: 'Project',
    scope: 'OrgUnit',
    orgUnitId: 'ou-apac-sg',
    orgUnitName: 'Singapore Office',
    orgUnitPath: 'Global Technologies Inc. > APAC Region > Singapore Office',
    colourHex: '#3B82F6',
    isActive: true,
    activeFrom: '2023-02-01',
    activeTo: null,
    createdAt: '2023-02-01T00:00:00Z',
    updatedAt: '2023-02-01T00:00:00Z'
  }, {
    id: 'tag-005',
    name: 'Office Renovation',
    description: 'Related to office renovation projects',
    tagTypeId: 'tagtype-002',
    tagTypeName: 'Project',
    scope: 'OrgUnit',
    orgUnitId: 'ou-latam-mx',
    orgUnitName: 'Mexico Office',
    orgUnitPath: 'Global Technologies Inc. > LATAM Region > Mexico Office',
    colourHex: '#8B5CF6',
    isActive: true,
    activeFrom: '2023-02-15',
    activeTo: '2023-12-31',
    createdAt: '2023-02-15T00:00:00Z',
    updatedAt: '2023-02-15T00:00:00Z'
  }]);
  const [tagTypes, setTagTypes] = useState<TagType[]>([{
    id: 'tagtype-001',
    name: 'Priority',
    description: 'Indicates the priority level of an item',
    scope: 'Global',
    isRestricted: true,
    colourPalette: ['#EF4444', '#F59E0B', '#10B981'],
    createdAt: '2023-01-01T00:00:00Z',
    updatedAt: '2023-01-01T00:00:00Z'
  }, {
    id: 'tagtype-002',
    name: 'Project',
    description: 'Associates records with specific projects',
    scope: 'Global',
    isRestricted: false,
    colourPalette: ['#3B82F6', '#8B5CF6', '#EC4899', '#F59E0B'],
    createdAt: '2023-02-01T00:00:00Z',
    updatedAt: '2023-02-01T00:00:00Z'
  }, {
    id: 'tagtype-003',
    name: 'Department',
    description: 'Indicates the department responsible for the record',
    scope: 'OrgUnit',
    orgUnitId: 'ou-apac-sg',
    orgUnitName: 'Singapore Office',
    orgUnitPath: 'Global Technologies Inc. > APAC Region > Singapore Office',
    isRestricted: true,
    colourPalette: ['#10B981', '#6366F1', '#F97316', '#8B5CF6'],
    createdAt: '2023-03-01T00:00:00Z',
    updatedAt: '2023-03-01T00:00:00Z'
  }]);
  const handleAddTag = () => {
    setEditingTag(null);
    setShowTagForm(true);
  };
  const handleEditTag = (tag: Tag) => {
    setEditingTag(tag);
    setShowTagForm(true);
  };
  const handleSaveTag = (tag: Omit<Tag, 'id' | 'createdAt' | 'updatedAt'>) => {
    if (editingTag) {
      // Update existing tag
      setTags(tags.map(t => t.id === editingTag.id ? {
        ...editingTag,
        ...tag,
        updatedAt: new Date().toISOString()
      } : t));
    } else {
      // Create new tag
      const newTag: Tag = {
        id: `tag-${String(tags.length + 1).padStart(3, '0')}`,
        ...tag,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      setTags([...tags, newTag]);
    }
    setShowTagForm(false);
    setEditingTag(null);
  };
  const handleToggleTagActive = (id: string) => {
    setTags(tags.map(tag => tag.id === id ? {
      ...tag,
      isActive: !tag.isActive,
      updatedAt: new Date().toISOString()
    } : tag));
  };
  const handleDeleteTag = (id: string) => {
    setTags(tags.filter(tag => tag.id !== id));
  };
  const handleAddTagType = () => {
    setEditingTagType(null);
    setShowTagTypeForm(true);
  };
  const handleEditTagType = (tagType: TagType) => {
    setEditingTagType(tagType);
    setShowTagTypeForm(true);
  };
  const handleSaveTagType = (tagType: Omit<TagType, 'id' | 'createdAt' | 'updatedAt'>) => {
    if (editingTagType) {
      // Update existing tag type
      setTagTypes(tagTypes.map(tt => tt.id === editingTagType.id ? {
        ...editingTagType,
        ...tagType,
        updatedAt: new Date().toISOString()
      } : tt));
      // Update tag type name in associated tags
      setTags(tags.map(tag => tag.tagTypeId === editingTagType.id ? {
        ...tag,
        tagTypeName: tagType.name,
        updatedAt: new Date().toISOString()
      } : tag));
    } else {
      // Create new tag type
      const newTagType: TagType = {
        id: `tagtype-${String(tagTypes.length + 1).padStart(3, '0')}`,
        ...tagType,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      setTagTypes([...tagTypes, newTagType]);
    }
    setShowTagTypeForm(false);
    setEditingTagType(null);
  };
  const handleDeleteTagType = (id: string) => {
    // Check if tag type is in use
    const inUse = tags.some(tag => tag.tagTypeId === id);
    if (inUse) {
      alert('This tag type is in use by one or more tags and cannot be deleted.');
      return;
    }
    setTagTypes(tagTypes.filter(tagType => tagType.id !== id));
  };
  const handleCancelTagForm = () => {
    setShowTagForm(false);
    setEditingTag(null);
  };
  const handleCancelTagTypeForm = () => {
    setShowTagTypeForm(false);
    setEditingTagType(null);
  };
  // Separate global and orgunit items
  const globalTags = tags.filter(t => t.scope === 'Global');
  const orgUnitTags = tags.filter(t => t.scope === 'OrgUnit');
  const globalTagTypes = tagTypes.filter(tt => tt.scope === 'Global');
  const orgUnitTagTypes = tagTypes.filter(tt => tt.scope === 'OrgUnit');
  return <div data-id="element-20628">
      <div className="mb-8" data-id="element-20629">
        <h2 className="text-2xl font-bold text-gray-900" data-id="element-20630">Tags Management</h2>
        <p className="mt-1 text-sm text-gray-500" data-id="element-20631">
          Create and manage tags at global and organisation unit levels
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6" data-id="element-20632">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs" data-id="element-20633">
          <button className={`${activeView === 'tags' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`} onClick={() => setActiveView('tags')} data-id="element-20634">
            Tags
          </button>
          <button className={`${activeView === 'tagTypes' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`} onClick={() => setActiveView('tagTypes')} data-id="element-20635">
            Tag Types
          </button>
        </nav>
      </div>

      {/* Content based on active view */}
      {activeView === 'tags' && <>
          {showTagForm ? <TagForm tag={editingTag} tagTypes={tagTypes} onSave={handleSaveTag} onCancel={handleCancelTagForm} locale={locale} data-id="element-20636" /> : <TagsList globalTags={globalTags} orgUnitTags={orgUnitTags} onAdd={handleAddTag} onEdit={handleEditTag} onToggleActive={handleToggleTagActive} onDelete={handleDeleteTag} locale={locale} data-id="element-20637" />}
        </>}

      {activeView === 'tagTypes' && <>
          {showTagTypeForm ? <TagTypeForm tagType={editingTagType} onSave={handleSaveTagType} onCancel={handleCancelTagTypeForm} locale={locale} data-id="element-20638" /> : <TagTypesList globalTagTypes={globalTagTypes} orgUnitTagTypes={orgUnitTagTypes} onAdd={handleAddTagType} onEdit={handleEditTagType} onDelete={handleDeleteTagType} locale={locale} data-id="element-20639" />}
        </>}
    </div>;
}